import { Component, h, Element, Prop, Watch, Method } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { unescapeHTML } from '../../global/utility/unescape-html';
import { isMagnoliaEditingMode } from '../../helper';
import { Link } from '../ui-cms-link/ui-cms-link';
import { createAbsoutePathIfRelative } from '../../global/utility/tracking-helpers';

export interface UiCmsTeaserBody {
  headline?: string;
  teaserText?: string;
  date?: string;
  category?: string;
  author?: string;
  image?: string;
  imageAltText?: string;
  imageMouseover?: string;
  imageCopyright?: string;
  imageRatio?: string;
  links: Link[];
  teaserStyle?:
    | 'primary'
    | 'primary100'
    | 'secondary100'
    | 'white'
    | 'border'
    | 'transparent-overlay'
    | 'transparent-no-indentation';
  isTextFloating?: boolean;
  mediaSelection: 'none' | 'image' | 'video';
  videoSrc?: string;
  videoCopyright?: string;
  videoLoopSetting?: string;
}

@Component({
  tag: 'ui-cms-teaser',
  styleUrl: 'ui-cms-teaser.scss',
})
export class UiCmsTeaser {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsTeaserBody;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop({ mutable: true }) i18n = {
    videoEditorInfoMessage: "Im Author-Modus werden Videos in 'Dauerschleife mit Ton' ohne Ton dargestellt.",
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Prop() alignButton = false;

  @Method()
  async hasLinks() {
    return this.body?.links?.length > 0;
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  onLinkClick(event: CustomEvent<{ link: Link }>) {
    const { link } = event.detail;

    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Teaser',
        eventType: 'Action',
        eventCaller: this.el,
      },
      attributes: {
        elementType: 'Teaser',
        elementLabel: link.title,
        target: createAbsoutePathIfRelative(link.src),
      },
      category: {
        area: 'Content',
      },
    };

    if (trackingEvent) {
      window.ui?.services?.tracking?.track(trackingEvent);
    }
  }

  render() {
    const videoMutedByEditorMode = isMagnoliaEditingMode() && this.body.videoLoopSetting === 'loop-with-sound';

    return (
      <ui-teaser
        headline={unescapeHTML(this.body.headline)}
        text={unescapeHTML(this.body.teaserText)}
        links={this.body.links}
        date={
          this.body.date
            ? new Date(this.body.date).toLocaleDateString(['de', 'en'], {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })
            : undefined
        }
        sizes={window.ui.cmsState.isB2b ? '2400' : undefined}
        author={unescapeHTML(this.body.author)}
        category={unescapeHTML(this.body.category)}
        variant={this.body.teaserStyle}
        copyright={this.body.mediaSelection !== 'none' ? this.body[`${this.body.mediaSelection}Copyright`] : ''}
        isTextFloating={this.body.isTextFloating}
        mediaSelection={this.body.mediaSelection}
        videoSrc={this.body.videoSrc}
        videoLoopSetting={videoMutedByEditorMode ? 'loop-muted' : this.body.videoLoopSetting || 'no-loop'}
        image={this.body.image}
        imageAltText={this.body.imageAltText}
        imageRatio={this.body.imageRatio}
        imageMouseover={this.body.imageMouseover}
        replace="%RENDITION%"
        alignButton={!this.body.isTextFloating ? this.alignButton : false}
        onLinkClick={this.onLinkClick.bind(this)}
      >
        {videoMutedByEditorMode ? <p class="video-info-message">{this.i18n.videoEditorInfoMessage}</p> : null}
      </ui-teaser>
    );
  }
}
